import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, X } from "lucide-react";
import RecommendationsRail from "./RecommendationsRail";

/**
 * Auto-popping drawer that appears whenever any product is added to
 * cart. Listens to the global `kb:cart-added` CustomEvent that the
 * CartContext dispatches on every successful add, snapshots the
 * triggering product_id, and shows a "Frequently bought together"
 * rail driven by `/api/recommendations/product/{id}`.
 *
 * Mounted ONCE at the App-shell root (in App.js, near the toaster).
 * Self-dismisses after a short delay or on user click. Designed to be
 * unobtrusive — slides in from the right, doesn't block the page.
 */
export default function AddToCartDrawer() {
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState(null);
  const [productName, setProductName] = useState("");
  const dismissTimer = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      // Some legacy events only carry a name — without a product_id we
      // can still show the toast confirmation rail; we fall back to
      // the cart-aware recommendations endpoint by passing null.
      setProductId(detail.product_id || detail.id || null);
      setProductName(detail.name || "Item added to bag");
      setOpen(true);
      // Auto-dismiss after 9s so a customer who keeps shopping isn't
      // permanently stuck with a drawer in their face.
      clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => setOpen(false), 9000);
    };
    window.addEventListener("kb:cart-added", handler);
    return () => {
      window.removeEventListener("kb:cart-added", handler);
      clearTimeout(dismissTimer.current);
    };
  }, []);

  const close = () => {
    clearTimeout(dismissTimer.current);
    setOpen(false);
  };

  return (
    <>
      {/* Backdrop — clicking it dismisses. Pointer-events off when
          closed so the page stays interactive. */}
      <div
        className={`fixed inset-0 z-[100] bg-black/30 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={close}
        aria-hidden
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[101] shadow-2xl transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        } flex flex-col safe-pt safe-pb safe-pr`}
        role="dialog"
        aria-label="Item added to cart"
        data-testid="add-to-cart-drawer"
        data-open={open ? "true" : "false"}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--kb-border))]">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={18} className="text-green-600" />
            <div>
              <div className="font-semibold text-sm" data-testid="atc-drawer-product-name">{productName}</div>
              <div className="text-[11px] text-[hsl(var(--kb-muted))]">Added to your bag</div>
            </div>
          </div>
          <button
            onClick={close}
            className="p-1 hover:bg-[hsl(var(--kb-surface-alt))] rounded"
            aria-label="Close"
            data-testid="atc-drawer-close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <RecommendationsRail
            variant={productId ? "product" : "cart"}
            productId={productId}
            title="Customers also bought"
            limit={6}
            data-testid="atc-drawer-rail"
          />
        </div>

        <footer className="border-t border-[hsl(var(--kb-border))] p-3 flex gap-2">
          <button
            onClick={close}
            className="kb-btn-outline flex-1 text-sm py-2"
            data-testid="atc-drawer-keep-shopping"
          >
            Keep shopping
          </button>
          <Link
            to="/cart"
            onClick={close}
            className="kb-btn-primary flex-1 text-sm py-2 text-center"
            data-testid="atc-drawer-view-cart"
          >
            View bag
          </Link>
        </footer>
      </aside>
    </>
  );
}
